<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { faCompress, faExpand } from '@fortawesome/duotone-regular-svg-icons'
import useFullscreen from '~/composables/useFullscreen'
import Button from '~/components/ui/Button.vue'

const emit = defineEmits<{ fullscreen: [boolean] }>()
const { t } = useI18n()
const {
  isSupported,
  toggleFullscreen,
  isFullscreen,
  disableFullscreen,
  onFullscreenChange,
  setCurrentFullscreenState,
} = useFullscreen()

const browserWidth = ref<number>()

const buttonBreakpoint = 767 // Allow small tablets to access fullscreen
const ariaLabel = computed(() => isFullscreen.value ? t('fullscreen.close') : t('fullscreen.open'))
const showButton = computed(() => (browserWidth.value && browserWidth.value > buttonBreakpoint) || isFullscreen.value)

onMounted(() => {
  if (isSupported()) {
    addEventListener('fullscreenchange', onFullscreenChange)
    setCurrentFullscreenState()
    addEventListener('resize', updateWidth)
    updateWidth()
  }
})

onUnmounted(() => {
  if (isSupported()) {
    removeEventListener('fullscreenchange', onFullscreenChange)
    removeEventListener('resize', updateWidth)
    disableFullscreen()
  }
})

const updateWidth = () => {
  browserWidth.value = window.innerWidth
}

watch(isFullscreen, value => emit('fullscreen', value))
</script>

<template>
  <Button
    v-if="isSupported() && showButton"
    variant="outline"
    shape="square"
    size="xl"
    :aria-label="ariaLabel"
    @click="toggleFullscreen"
  >
    <font-awesome-icon :icon="isFullscreen ? faCompress : faExpand" style="--fa-secondary-opacity: 0" />
  </Button>
</template>
